import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../../../../../../components/Modal/CustomModal';
import {
    getCalculationTypeById,
    getConflictResolutionById, getStackMethodById,
    POLICY_CONDITION_MODEL, STACK_METHOD_MODEL
} from '../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model';
import CompoundBreakdownTable from '../SubComponents/CompoundBreakdownTable';
import HourBreakdownTable from '../SubComponents/HourBreakdownTable';
import { currencyFormat } from '../../../../../../../Apis/functions';
import AdditiveBreakdownTable from "../SubComponents/AdditiveBreakdownTable";
import BreakdownScopeDetail from "../SubComponents/BreakdownScopeDetail";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Detail modal for an overtime line. Receives only the line `item` and derives
 * every displayed value from it: the policy condition info and the per-period
 * rate breakdown table. The table layout depends on the stack method.
 *
 * @param {boolean} show - Controls modal visibility.
 * @param {Function} onHide - Callback to close the modal.
 * @param {Object} item - Overtime line snapshot the breakdown is built from.
 */
const OvertimeBreakdownModal = ({ show, onHide, item }) => {
    const { t } = useTranslation();

    const condition = POLICY_CONDITION_MODEL[item?.policyConditionSnapshot];
    const type = getCalculationTypeById(item?.policyCalculationTypeSnapshot);
    const isCompound = item?.groupStackMethodSnapshot === STACK_METHOD_MODEL.COMPOUND;
    // Bonus lines are not built from attendance entries, so they only show the policy detail.
    const isBonus = item?.policyConditionSnapshot === POLICY_CONDITION_MODEL.BONUS.key;

    return (
        <CustomModal
            title={"breakdown"}
            show={show}
            size='lg'
            onHide={onHide}
            onClose={onHide}
            cancelButton={false}
            confirmButton={false}
        >
            <p className='fs-14px ibl-font-w-600 mb-2'>{t('condition').toUpperCase()}</p>
            <p className='fs-14px mb-1'><span className='ibl-font-w-600'>{t('trigger_logic')}:</span> {t(condition?.trans)}</p>
            <p className='fs-14px mb-1'><span className='ibl-font-w-600'>{t('conflict_resolution')}:</span> {t(getConflictResolutionById(item?.groupConflictResolutionSnapshot)?.name)}</p>
            {item?.groupStackMethodSnapshot &&
                <p className='fs-14px mb-1'><span className='ibl-font-w-600'>{t('stack_method')}:</span> {t(getStackMethodById(item?.groupStackMethodSnapshot)?.name)}</p>
            }
            <BreakdownScopeDetail item={item} />

            <p className='fs-14px ibl-font-w-600 mt-3 mb-2'>{t('calculation').toUpperCase()}</p>
            <p className='fs-14px mb-1'><span className='ibl-font-w-600'>{t('calculation_type')}:</span> {t(type?.name)}</p>
            <p className='fs-14px mb-1'><span className='ibl-font-w-600'>{t('calculation_value')}:</span> {type?.prefixText}{item?.policyCalculationValueSnapshot}</p>

            {!isBonus && <>
                <p className='fs-14px ibl-font-w-600 mt-4'>{t('attendance_entries').toUpperCase()}</p>
                {item?.policyConditionSnapshot === POLICY_CONDITION_MODEL.HOUR.key ?
                    <HourBreakdownTable item={item} />
                    :
                    (isCompound)
                        ? <CompoundBreakdownTable item={item} />
                        : <AdditiveBreakdownTable item={item} />
                }
            </>}

            <div className='overtime-breakdown-total d-flex justify-content-between align-items-center'>
                <span className='fs-14px ibl-font-w-700'>{t('total').toUpperCase()}</span>
                <span className='fs-14px ibl-font-w-700'>{`$${currencyFormat(item?.result)}`}</span>
            </div>
        </CustomModal>
    );
};

export default OvertimeBreakdownModal;
